import React, { useState } from 'react';
import {
  Pencil,
  Play,
  Loader2,
  Users,
  Layers,
  FileText,
  Target,
  ClipboardList,
  FlaskConical,
  TestTube,
  Package,
  TrendingUp,
  MousePointer,
  MessageSquare,
  CheckCircle,
  XCircle,
  Car,
  Pill,
} from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { createSimulation, handleApiError } from '../../utils/simulationApi';
import { useAuth } from '../../context/AuthContext';

type Step =
  | 'dashboard'
  | 'use-case-form'
  | 'select-audience'
  | 'select-segments'
  | 'review-and-run'
  | 'simulation-results';

interface ReviewAndRunProps {
  useCaseType: string;
  useCaseTitle: string;
  audienceName: string;
  audienceId: number;
  segmentNames: string[];
  selectedSegmentIds: number[];
  personaFilters: Record<number, any>;
  formPayload: Record<string, any>;
  onEdit: (step: Step) => void;
  onComplete: (simulationId: number) => void;
}

const USE_CASE_OUTPUTS: Record<string, string> = {
  'concept-test': 'Ranked concepts, audience reactions, purchase intent prediction',
  'driving-concept-test': 'D-scores (See, Understand, Respond, Capability), segment analysis, driver insights',
  'vitamin-concept-test': 'Overall concept score, purchase intent, believability, segment breakdown, friction & drivers',
  'claims-testing': 'Claim rankings, MaxDiff scores, audience preferences',
  'survey-and-focus-groups': 'Survey responses, qualitative insights, thematic analysis',
  'packaging-review-v2': 'Technical compliance, shelf test, persona reactions',
  'attribution': 'Message performance, conversion drivers, recommendations',
  'channel-strategy': 'Creative rankings, audience insights, performance predictions',
  'image-survey': 'Click rankings, creative effectiveness scores',
  'logo_test': 'Logo effectiveness, audience preferences, recommendations',
  'test-use-case': 'Simulation results and analysis',
};

const USE_CASE_ICONS: Record<string, React.ReactNode> = {
  'concept-test': <TestTube className="w-5 h-5" />,
  'driving-concept-test': <Car className="w-5 h-5" />,
  'vitamin-concept-test': <Pill className="w-5 h-5" />,
  'claims-testing': <FlaskConical className="w-5 h-5" />,
  'survey-and-focus-groups': <ClipboardList className="w-5 h-5" />,
  'packaging-review-v2': <Package className="w-5 h-5" />,
  'attribution': <Target className="w-5 h-5" />,
  'channel-strategy': <TrendingUp className="w-5 h-5" />,
  'image-survey': <MousePointer className="w-5 h-5" />,
};

const SKIP_KEYS = new Set([
  'audience_id', 'segment_ids', 'persona_filters',
  'task', 'name', 'description',
  'sample_size', 'population', 'llm_model',
]);

const DRIVING_SKIP_KEYS = new Set([
  'llm_model', 'population',
]);

const PACKAGING_SKIP_KEYS = new Set([
  'analysis_type', 'segments', 'goal_of_simulation',
]);

const FRIENDLY_LABELS: Record<string, string> = {
  name: 'Simulation Name',
  goal: 'Goal',
  goal_of_simulation: 'Goal',
  context: 'Context',
  product_context: 'Product Context',
  headline: 'Headline',
  consumer_insight: 'Consumer Insight',
  main_benefit: 'Main Benefit',
  reasons_to_believe: 'Reasons to Believe',
  price_point: 'Price Point',
  pack_size: 'Pack Size',
  brand: 'Brand',
  hero_image: 'Hero Image',
  alternative_products: 'Alternative Products',
  product_name: 'Product Name',
  product_description: 'Product Description',
  category: 'Category',
  claims: 'Claims',
  questions: 'Questions',
  marketing_copies: 'Messages to Test',
  objective: 'Objective',
  placement: 'Placement',
  placement_type: 'Placement Type',
  placement_goal: 'Placement Goal',
  creative_type: 'Creative Type',
  industry: 'Industry',
  images: 'Images',
  front_image: 'Front Panel Image',
  side_images: 'Side Panel Images',
  back_images: 'Back Panel Images',
  competitor_images: 'Competitor Images',
  design_guideline: 'Design Guideline',
  analysis_type: 'Analysis Type',
  price_tier: 'Price Tier',
  review_type: 'Review Type',
  campaign_objective: 'Campaign Objective',
  kpis: 'KPIs',
  company_details: 'Company Details',
  product_details: 'Product Details',
  competitive_landscape: 'Competitive Landscape',
  medium_and_placement: 'Medium & Placement',
  cultural_considerations: 'Cultural Considerations',
  prior_performance_data: 'Prior Performance Data',
  use_internal_debate: 'Internal Debate',
  enable_adaptive_persona_context: 'Adaptive Persona Context',
  use_parsed_questions: 'Pre-parsed Questions',
  message_to_test: 'Message to Test',
  segments: 'Segments Detail',
  current_logo: 'Current Logo',
  image_descriptions: 'Image Descriptions',
  concept_statement: 'Concept Statement',
  sample_size: 'Number of Agents',
  population: 'Population',
  weather: 'Weather',
  traffic: 'Traffic',
  llm_model: 'AI Model',
  product_headline: 'Product Headline',
  brand_name: 'Brand Name',
  product_image_url: 'Product Image URL',
  competitor_products: 'Competitor Products',
};

function friendlyLabel(key: string): string {
  if (FRIENDLY_LABELS[key]) return FRIENDLY_LABELS[key];
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function isBase64Image(val: unknown): val is string {
  return typeof val === 'string' && val.startsWith('data:image');
}

function isUrl(val: unknown): val is string {
  return typeof val === 'string' && (val.startsWith('http://') || val.startsWith('https://'));
}

function isImageValue(val: unknown): boolean {
  return isBase64Image(val) || isUrl(val);
}

// Render a single value smartly based on its type
const ValueRenderer: React.FC<{ value: unknown; fieldKey?: string }> = ({ value, fieldKey }) => {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400 italic text-sm">Not provided</span>;
  }

  // Boolean
  if (typeof value === 'boolean') {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm">
        {value
          ? <><CheckCircle className="w-4 h-4 text-green-500" /> <span className="text-green-700 font-medium">Enabled</span></>
          : <><XCircle className="w-4 h-4 text-gray-400" /> <span className="text-gray-500">Disabled</span></>
        }
      </span>
    );
  }

  // Number
  if (typeof value === 'number') {
    return <span className="text-sm text-gray-900 font-medium">{value}</span>;
  }

  // Single base64 image
  if (isBase64Image(value)) {
    return (
      <img src={value} alt={fieldKey || 'Image'} className="h-20 w-20 object-cover rounded-lg border border-gray-200" />
    );
  }

  // URL image
  if (isUrl(value)) {
    return (
      <img src={value} alt={fieldKey || 'Image'} className="h-20 w-20 object-cover rounded-lg border border-gray-200" />
    );
  }

  // Hero image object { image, description }
  if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
    const obj = value as Record<string, unknown>;
    if ('image' in obj && (isBase64Image(obj.image) || obj.image === null)) {
      return (
        <div className="flex items-center gap-3">
          {isBase64Image(obj.image) ? (
            <img src={obj.image as string} alt="Hero" className="h-20 w-20 object-cover rounded-lg border border-gray-200" />
          ) : (
            <span className="text-gray-400 italic text-sm">No image uploaded</span>
          )}
          {obj.description ? (
            <span className="text-sm text-gray-600 italic">{String(obj.description)}</span>
          ) : null}
        </div>
      );
    }
  }

  // Array of images (base64 or URL)
  if (Array.isArray(value) && value.length > 0 && value.every(v => isImageValue(v))) {
    return (
      <div className="flex flex-wrap gap-2">
        {value.map((img, idx) => (
          <img key={idx} src={img} alt={`Image ${idx + 1}`} className="h-16 w-16 object-cover rounded-lg border border-gray-200" />
        ))}
      </div>
    );
  }

  // Array of strings (short items → pills, long items → numbered list)
  if (Array.isArray(value) && value.length > 0 && value.every(v => typeof v === 'string')) {
    const filtered = value.filter(v => v.trim() !== '');
    if (filtered.length === 0) return <span className="text-gray-400 italic text-sm">None</span>;
    const avgLength = filtered.reduce((sum, v) => sum + v.length, 0) / filtered.length;
    if (avgLength > 60 || filtered.length > 5) {
      return (
        <ol className="space-y-1.5 list-none">
          {filtered.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-gray-800">
              <span className="text-gray-400 font-mono text-xs mt-0.5 flex-shrink-0">{idx + 1}.</span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
      );
    }
    return (
      <div className="flex flex-wrap gap-1.5">
        {filtered.map((item, idx) => (
          <span key={idx} className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">{item}</span>
        ))}
      </div>
    );
  }

  // Array of objects (questions, segments, etc.)
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
    return (
      <span className="text-sm text-gray-900 font-medium">{value.length} item{value.length !== 1 ? 's' : ''}</span>
    );
  }

  // Generic object → key-value pairs
  if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v !== null && v !== undefined && v !== ''
    );
    if (entries.length === 0) return <span className="text-gray-400 italic text-sm">Empty</span>;
    return (
      <div className="space-y-2 pl-3 border-l-2 border-gray-200">
        {entries.map(([k, v]) => (
          <div key={k}>
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{friendlyLabel(k)}</span>
            <div className="mt-0.5"><ValueRenderer value={v} fieldKey={k} /></div>
          </div>
        ))}
      </div>
    );
  }

  // Long string (> 120 chars) → expandable
  if (typeof value === 'string' && value.length > 120) {
    return <ExpandableText text={value} />;
  }

  // Short string
  return <span className="text-sm text-gray-900">{String(value)}</span>;
};

const ExpandableText: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <p className={`text-sm text-gray-900 whitespace-pre-wrap ${expanded ? '' : 'line-clamp-2'}`}>{text}</p>
      <button onClick={() => setExpanded(!expanded)} className="text-xs text-blue-600 hover:text-blue-800 mt-1 font-medium">
        {expanded ? 'Show less' : 'Show more'}
      </button>
    </div>
  );
};

function flattenPayload(payload: Record<string, any>): { key: string; label: string; value: unknown }[] {
  const items: { key: string; label: string; value: unknown }[] = [];

  for (const [key, value] of Object.entries(payload)) {
    if (SKIP_KEYS.has(key)) continue;
    if (value === null || value === undefined || value === '') continue;

    // For 'data' key (claims-testing, packaging-review-v2), flatten its children
    if (key === 'data' && typeof value === 'object' && !Array.isArray(value)) {
      for (const [childKey, childVal] of Object.entries(value as Record<string, unknown>)) {
        if (childVal === null || childVal === undefined || childVal === '') continue;
        if (SKIP_KEYS.has(childKey)) continue;
        items.push({ key: childKey, label: friendlyLabel(childKey), value: childVal });
      }
      continue;
    }

    items.push({ key, label: friendlyLabel(key), value });
  }

  return items;
}

const ReviewAndRun: React.FC<ReviewAndRunProps> = ({
  useCaseType,
  useCaseTitle,
  audienceName,
  audienceId,
  segmentNames,
  selectedSegmentIds,
  personaFilters,
  formPayload,
  onEdit,
  onComplete,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const { isAdmin } = useAuth();
  const isDriving = useCaseType === 'driving-concept-test';
  const isVitamin = useCaseType === 'vitamin-concept-test';
  const isPackaging = useCaseType === 'packaging-review-v2';
  const isExternalConceptTest = isDriving || isVitamin;

  const VITAMIN_SKIP_KEYS = new Set(['llm_model']);
  const ADMIN_ONLY_KEYS = new Set(['sample_size', 'population']);
  const outputs = USE_CASE_OUTPUTS[useCaseType] || 'Simulation results and analysis';
  const useCaseIcon = USE_CASE_ICONS[useCaseType] || <FileText className="w-5 h-5" />;
  const skipKeys = isDriving ? DRIVING_SKIP_KEYS : isVitamin ? VITAMIN_SKIP_KEYS : isPackaging ? PACKAGING_SKIP_KEYS : null;
  const payloadItems = flattenPayload(formPayload)
    .filter(item => !(skipKeys && skipKeys.has(item.key)))
    .filter(item => isAdmin || !ADMIN_ONLY_KEYS.has(item.key));

  const filtersApplied = Object.values(personaFilters).some((filters: any) =>
    Object.values(filters).some((vals: any) => Array.isArray(vals) && vals.length > 0)
  );

  const handleRun = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError('');

    try {
      const finalPayload = isExternalConceptTest
        ? { ...formPayload }
        : {
            ...formPayload,
            audience_id: audienceId,
            segment_ids: selectedSegmentIds,
            persona_filters: personaFilters,
          };

      const data = await createSimulation(finalPayload);
      onComplete(data.simulation_id);
    } catch (err) {
      console.error('Error starting simulation:', err);
      handleApiError(err, setError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto animate-fadeIn">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Review & Run</h1>
      <p className="text-gray-500 mb-6">Make sure everything looks good before running your simulation.</p>

      {error && (
        <div className="text-sm text-red-600 mb-4 bg-red-50 border border-red-200 p-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {/* Use Case */}
        <Card hoverable={false} fullWidth>
          <div className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Use Case</h3>
              <button onClick={() => onEdit('dashboard')} className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors">
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50 text-blue-600">{useCaseIcon}</div>
              <div>
                <p className="text-base font-medium text-gray-900">{useCaseTitle}</p>
                {!isDriving && !isPackaging && <p className="text-sm text-gray-500">{outputs}</p>}
              </div>
            </div>
          </div>
        </Card>

        {/* Study Inputs — generic renderer */}
        {payloadItems.length > 0 && (
          <Card hoverable={false} fullWidth>
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Study Inputs</h3>
                <button onClick={() => onEdit('use-case-form')} className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors">
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
              </div>
              <div className="space-y-4">
                {payloadItems.map((item) => (
                  <div key={item.key} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">{item.label}</p>
                    <ValueRenderer value={item.value} fieldKey={item.key} />
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Audience — hidden for external concept tests */}
        {!isExternalConceptTest && (
          <Card hoverable={false} fullWidth>
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Audience</h3>
                <button onClick={() => onEdit('select-audience')} className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors">
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-50 text-purple-600">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-base font-medium text-gray-900">{audienceName || '—'}</p>
                  <p className="text-sm text-gray-500">ID: {audienceId}</p>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Segments — hidden for external concept tests */}
        {!isExternalConceptTest && (
          <Card hoverable={false} fullWidth>
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Segments</h3>
                <button onClick={() => onEdit('select-segments')} className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors">
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-green-50 text-green-600">
                  <Layers className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  {segmentNames.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {segmentNames.map((name, idx) => (
                        <span key={idx} className="px-3 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full text-sm font-medium">
                          {name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-900">{selectedSegmentIds.length} segment{selectedSegmentIds.length !== 1 ? 's' : ''} selected</p>
                  )}
                  {filtersApplied && (
                    <p className="text-xs text-amber-600 mt-2 font-medium">Persona filters applied</p>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Expected Outputs — hidden for external concept tests and packaging */}
        {!isExternalConceptTest && !isPackaging && (
          <Card hoverable={false} fullWidth>
            <div className="p-5">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Expected Outputs</h3>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <p className="text-sm text-gray-900">{outputs}</p>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Run button */}
      <div className="mt-8 flex justify-end">
        <Button
          variant="primary"
          size="lg"
          onClick={handleRun}
          disabled={isSubmitting}
          icon={isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
        >
          {isSubmitting ? 'Starting simulation...' : `Run ${useCaseTitle}`}
        </Button>
      </div>
    </div>
  );
};

export default ReviewAndRun;
